class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if ('visualViewport' in window && window?.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (this.isOpen && this.predictiveSearchResults) {
          this.predictiveSearchResults.style.maxHeight = this.getResultsMaxHeight();
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    const customUrl = this.getCustomExactMatch(this.getQuery());
    if (customUrl) {
      event.preventDefault();
      window.location.href = customUrl;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
}

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.textContent;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (activeElement) activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    if (activeElement) this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
      // If a suggestion is highlighted via arrow keys, click it
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedOption) {
        selectedOption.click();
        return;
      }

      // Otherwise check if the typed query matches a custom term exactly
      const customUrl = this.getCustomExactMatch(this.searchTerm);
      if (customUrl) {
        window.location.href = customUrl;
        return;
      }

      // Fall through to default Shopify search
      this.input.form.submit();
  }

  getCustomExactMatch(query) {
      const terms = window.__customSearchTerms;
      const base = window.__customSearchBase || '';
      if (!terms || !query) return null;

      const q = query.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      // Check for exact match first
      for (const [label, url] of Object.entries(terms)) {
        const normalized = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (normalized === q) {
          return url.startsWith('http') ? url : base + url;
        }
      }

      // If only one suggestion matches, use that
      const matches = [];
      for (const [label, url] of Object.entries(terms)) {
        const normalized = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (normalized.includes(q)) {
          matches.push({ label, url });
        }
      }
      if (matches.length === 1) {
        const url = matches[0].url;
        return url.startsWith('http') ? url : base + url;
      }

      return null;
    }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=page,query,product,collection&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

renderSearchResults(resultsMarkup) {
    const customHTML = this.getCustomSuggestions(this.searchTerm);
    
    // Parse the markup so we can remove conflicting Shopify page results
    if (customHTML) {
      const parser = new DOMParser();
      const doc = parser.parseFromString('<div>' + resultsMarkup + '</div>', 'text/html');
      
      // Get custom term labels (lowercased) to match against
      const customLabels = this.getCustomLabels(this.searchTerm);
      
      // Remove Shopify page/query results that match a custom term
      doc.querySelectorAll('.predictive-search__list-item').forEach(li => {
        const link = li.querySelector('a');
        if (!link) return;
        const text = link.textContent.trim().toLowerCase();
        if (customLabels.has(text)) {
          li.remove();
        }
      });
      
      resultsMarkup = doc.querySelector('div').innerHTML;
    }
    
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    
    if (customHTML) {
      const wrapper = this.predictiveSearchResults.querySelector('#predictive-search-results');
      if (wrapper) {
        wrapper.insertAdjacentHTML('afterbegin', customHTML);
      } else {
        this.predictiveSearchResults.insertAdjacentHTML('afterbegin', customHTML);
      }
    }
    
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
}

getCustomLabels(query) {
    const terms = window.__customSearchTerms;
    if (!terms || !query) return new Set();
    const q = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const labels = new Set();
    for (const label of Object.keys(terms)) {
      const normalized = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalized.includes(q)) {
        labels.add(label.toLowerCase());
      }
    }
    return labels;
}

getCustomSuggestions(query) {
    const terms = window.__customSearchTerms;
    const base = window.__customSearchBase || '';
    if (!terms || !query) return '';
    
    const q = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const matches = [];
    
    for (const [label, url] of Object.entries(terms)) {
      const normalized = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalized.includes(q)) {
        const fullUrl = url.startsWith('http') ? url : base + url;
        matches.push({ label, url: fullUrl, startsWith: normalized.startsWith(q) });
      }
    }
    
    // Sort: starts-with first, then alphabetical
    matches.sort((a, b) => {
      if (a.startsWith && !b.startsWith) return -1;
      if (!a.startsWith && b.startsWith) return 1;
      return a.label.localeCompare(b.label, 'fr');
    });

    // Deduplicate by URL — keep only the first (best) match per URL
    const seen = new Set();
    const deduped = matches.filter(m => {
        if (seen.has(m.url)) return false;
        seen.add(m.url);
        return true;
    });
    
    
    if (deduped.length === 0) return '';
    
    let html = '<div class="predictive-search__custom-suggestions">';
    html += '<h2 class="predictive-search__heading text-body">Pages & Services</h2>';
    html += '<ul class="predictive-search__results-list list-unstyled" role="group">';
    
    deduped.slice(0, 5).forEach((m, i) => {
      html += `<li id="predictive-search-option-custom-${i}" 
                   class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${m.url}" class="predictive-search__item" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            ${m.label}
          </div>
        </a>
      </li>`;
    });
    
    html += '</ul></div>';
    
    return html;
}

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const $header = document.querySelector('.section-header');
    const headerBottom = $header?.getBoundingClientRect()?.bottom || 0;
    const $inputContainer = document.querySelector('.js-search-input-container');
    const inputHeight = $inputContainer?.offsetHeight || 0;
    
    if ('visualViewport' in window && window?.visualViewport) {
      const keyboardHeight = window.innerHeight - window.visualViewport.height;

      if (keyboardHeight > 0) {
        const paddedHeight = headerBottom + inputHeight;  
        return `${window.visualViewport.height - paddedHeight}px`;
      }
    }
    
    return `${window.innerHeight - headerBottom}px`;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.getResultsMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
